import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import UplaodFeedbackWorkspace from "../home_screns/upload_from_device/Uplaod_Feedback_Workspace";
import { EnhancedTokenManager } from "../../../utils/tokenManager";
import { API_URLS, BASE_URL } from "../../../api/urls";
import { extractCardIdFromUrl } from "../../../utils/urlIdEncoder";
import AvatarIcon from "../../../assets/images/profile-avatar.svg";
import { FeedbackItem } from "../../../types/feedback.types";

interface DashboardOutletContext {
  onHideSidebar: (hide: boolean) => void;
}

interface RawUploadFeedback {
  id: number;
  url: string;
  screenshot_url: string | null;
  recording_url: string | null;
  voice_recording_url: string | null;
  message: string | null;
  status: "draft";
  created_at: string;
}

interface RawUploadCard {
  card_id: number;
  card_name: string;
  preview_screenshot: string | null;
  feedbacks: RawUploadFeedback[];
}

const Shimmer = ({ className }: { className: string }) => (
  <div
    className={className}
    style={{
      background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 2s ease-in-out infinite",
    }}
  />
);

const UploadPageSkeleton: React.FC = () => (
  <div className="h-screen w-full bg-white flex flex-col">
    <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>
    <Shimmer className="h-14 w-full" />
    <div className="flex flex-1">
      <Shimmer className="w-80 h-full" />
      <div className="flex-1 p-8 space-y-4">
        <Shimmer className="h-8 w-48 rounded" />
        <Shimmer className="h-4 w-64 rounded" />
        <Shimmer className="h-64 w-full rounded-xl mt-8" />
      </div>
    </div>
  </div>
);

const getValidUrl = (path: string | null | undefined): string | undefined => {
  if (!path) return undefined;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const UploadPage: React.FC = () => {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const { onHideSidebar } = useOutletContext<DashboardOutletContext>();

  const [loading, setLoading] = useState(true);
  const [cardName, setCardName] = useState("");
  const [workspaceId, setWorkspaceId] = useState(0);
  const [cardDbId, setCardDbId] = useState(0);
  const [initialFeedbackItems, setInitialFeedbackItems] = useState<FeedbackItem[]>([]);

  useEffect(() => {
    onHideSidebar(true);
    loadCard();
    return () => {
      onHideSidebar(false);
    };
  }, [cardId]);

  const loadCard = async () => {
    const numericCardId = extractCardIdFromUrl(cardId || '');
    if (!numericCardId) {
      navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
      return;
    }

    const token = EnhancedTokenManager.getAccessToken();
    if (!token) {
      navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
      return;
    }

    const wId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();
    if (!wId) {
      navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
      return;
    }
    setWorkspaceId(wId);

    try {
      const response = await fetch(API_URLS.GET_DRAFT(wId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch drafts");

      const data = await response.json();
      const entries = Object.entries(data) as [string, RawUploadCard][];
      const entry = entries.find(([, cd]) => cd.card_id === numericCardId);

      if (!entry) {
        navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
        return;
      }

      const [, cardData] = entry;
      setCardName(cardData.card_name);
      setCardDbId(cardData.card_id);

      const feedbacks = cardData.feedbacks
        .filter((f) => f.message || f.screenshot_url || f.recording_url || f.voice_recording_url)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      const feedbackItems: FeedbackItem[] = feedbacks.map((f, index) => {
        const screenshotUrl = getValidUrl(f.screenshot_url);
        const recordingUrl = getValidUrl(f.recording_url);
        const audioUrl = getValidUrl(f.voice_recording_url);
        const itemType: "screenshot" | "recording" = recordingUrl ? "recording" : "screenshot";

        const item: FeedbackItem = {
          id: f.id.toString(),
          type: itemType,
          timestamp: new Date(f.created_at).getTime(),
          order: index + 1,
          feedback: f.message || "",
          hasAudio: !!audioUrl,
          audioUrl,
          audioFilename: audioUrl ? `audio_${f.id}.wav` : undefined,
          user: { name: "User", avatar: AvatarIcon },
          isSelected: false,
          apiFeedbackId: f.id,
          uniqueFeedbackId: f.id.toString(),
        };

        if (itemType === "screenshot") {
          item.annotatedImage = screenshotUrl;
          item.originalImage = screenshotUrl;
        } else {
          item.dataUrl = recordingUrl;
          item.duration = 30;
          item.recordingBlob = new Blob([], { type: "video/mp4" });
        }

        return item;
      });

      setInitialFeedbackItems(feedbackItems);
      setLoading(false);
    } catch (error) {
      console.error("Error loading upload card:", error);
      navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
    }
  };

  if (loading) return <UploadPageSkeleton />;

  return (
    <UplaodFeedbackWorkspace
      feedbackName={cardName}
      onBack={() => navigate("/dashboard", { state: { activeTab: "draft" }, replace: true })}
      onSaveAndExit={(_items, _state) => {
        navigate("/dashboard", { replace: true, state: { activeTab: "draft", refreshTab: "draft" } });
      }}
      onSaveDraft={(_items, _state) => {
        navigate("/dashboard", { replace: true, state: { activeTab: "draft", refreshTab: "draft" } });
      }}
      onMinimize={() => navigate("/dashboard", { replace: true, state: { activeTab: "draft", refreshTab: "draft" } })}
      cardId={cardDbId}
      workspaceId={workspaceId}
      initialWorkspaceState={null}
      initialFeedbackItems={initialFeedbackItems.length > 0 ? initialFeedbackItems : undefined}
    />
  );
};

export default UploadPage;
